import { createSignal } from "solid-js"
import { createStore, reconcile, unwrap } from "solid-js/store"
import type { Prompt } from "./prompt"
import { createPromptRepository, PromptStorageError, type PromptDraft, type PromptRepository } from "@/storage/prompt-repository"

export type PromptSaveFailure = { title: string; description: string; retry: () => void }
const empty = (): PromptDraft => ({ prompt: [{ type: "text", content: "", start: 0, end: 0 }], context: { items: [] } })
const snapshot = (draft: PromptDraft): PromptDraft => ({
  prompt: draft.prompt.map((part) => ({ ...unwrap(part) })),
  context: { items: draft.context.items.map((item) => ({ ...unwrap(item) })) },
})

export function createPromptPersistence(input: {
  repository?: PromptRepository
} = {}) {
  const repository = input.repository ?? createPromptRepository()
  const pending = new Set<Promise<unknown>>()
  let disposed = false
  let submissions = 0
  const maybeDispose = () => {
    if (disposed && submissions === 0 && pending.size === 0) repository.dispose()
  }
  const track = <T>(promise: Promise<T>) => {
    pending.add(promise)
    const finish = () => { pending.delete(promise); maybeDispose() }
    void promise.then(finish, finish)
    return promise
  }
  const errorDescription = (error: unknown) => {
    if (error instanceof PromptStorageError) return error.message
    if (error instanceof DOMException && error.name === "QuotaExceededError") return "Browser storage is full. Your unsaved data is still in this tab. Free space, then retry before closing it."
    return "Your data could not be saved in this browser. Keep this tab open and retry."
  }

  const createDraft = (key: string) => {
    const [store, setStore] = createStore<PromptDraft>(empty())
    const [ready, setReady] = createSignal(false)
    let version: string = crypto.randomUUID()
    // The version storage holds, once known. Memory may be dropped only when it matches.
    let storedVersion: string | undefined
    let changed = false
    let openers = 0
    // Saves, history writes and sends whose source draft storage still keeps.
    let inFlight = 0
    let initialization: Promise<void>
    const [issue, setIssue] = createSignal<PromptSaveFailure>()
    const [submissionIssues, setSubmissionIssues] = createSignal<Record<string, PromptSaveFailure>>({})
    const report = (title: string, error: unknown, retry: () => void) => {
      if (disposed) return
      setIssue({ title, description: errorDescription(error), retry })
    }
    // Unused memory is dropped once loading storage again would show the same draft.
    const sweep = () => {
      if (openers > 0 || inFlight > 0 || storedVersion !== version) return
      if (drafts.get(key) === entry) drafts.delete(key)
    }
    const hold = (work: Promise<unknown>) => {
      inFlight++
      const done = () => { inFlight--; sweep() }
      void work.then(done, done)
    }
    const initialize = () => {
      initialization = track((async () => {
        const saved = await repository.readDraft(key)
        if (!changed) {
          if (saved) {
            version = saved.version
            setStore(reconcile(saved.draft))
          }
          storedVersion = version
          setIssue((current) => current?.title === "Draft could not be loaded" ? undefined : current)
        }
        setReady(true)
      })())
      hold(initialization)
      void initialization.catch((error) => {
        setReady(true)
        report("Draft could not be loaded", error, () => {
          void initialize().then(() => { if (changed) return save() }).catch(() => undefined)
        })
      })
      return initialization
    }
    void initialize()
    const save = () => {
      const savedVersion = version
      const value = snapshot(store)
      const job = track(initialization.then(() => repository.writeDraft(key, savedVersion, value)))
      hold(job.then(() => {
        storedVersion = savedVersion
        if (version === savedVersion) setIssue(undefined)
      }, (error) => report("Draft not saved", error, () => {
        // Retry current state, never the stale snapshot that originally failed.
        void initialize().then(save).catch(() => undefined)
      })))
      return job
    }
    const mutate = (change: () => void, persist = true) => {
      changed = true
      version = crypto.randomUUID()
      change()
      if (persist) void save()
    }
    const setPrompt = (prompt: Prompt) => mutate(() => setStore("prompt", prompt.map((part) => ({ ...unwrap(part) }))))
    const api = {
      store, ready, issue: () => issue() ?? Object.values(submissionIssues())[0],
      set: setPrompt,
      reset() { mutate(() => setStore("prompt", empty().prompt)) },
      setContext(items: PromptDraft["context"]["items"]) { mutate(() => setStore("context", "items", items)) },
      beginSubmission() {
        const submitted = snapshot(store)
        const submittedVersion = version
        const submissionID = crypto.randomUUID()
        const time = Date.now()
        let consumedVersion: string | undefined
        let accepted = false
        let retained = false
        const release = () => {
          if (!retained) return
          retained = false
          submissions--
          inFlight--
          maybeDispose()
          sweep()
        }
        return {
          release,
          clear() {
            if (!retained) { retained = true; submissions++; inFlight++ }
            // Keep the durable source draft until delivery is acknowledged.
            mutate(() => setStore("prompt", empty().prompt), false)
            consumedVersion = version
          },
          /** Puts a failed send back, unless any of the chat's composers changed the draft since `clear`. */
          restore(prompt: Prompt) {
            if (consumedVersion === undefined || version !== consumedVersion) return false
            setPrompt(prompt)
            return true
          },
          accept(mode: "normal", recordHistory = true) {
            if (accepted) return
            accepted = true
            const run = () => {
              const job = track((async () => {
                await initialization
                const removed = await repository.accept({ key, version: submittedVersion, submissionID, time, mode,
                  entry: recordHistory ? { prompt: submitted.prompt, comments: [] } : undefined })
                // Only clear submitted context if the user has not begun another draft.
                if (version === consumedVersion) {
                  setStore("context", "items", [])
                  // No stored draft loads as this empty one.
                  if (removed) storedVersion = version
                }
                setSubmissionIssues((current) => {
                  const { [submissionID]: _, ...remaining } = current
                  return remaining
                })
              })())
              hold(job)
              void job.catch((error) => {
                if (disposed) return
                setSubmissionIssues((current) => ({ ...current, [submissionID]: {
                  title: "Input history not saved",
                  description: errorDescription(error),
                  retry: () => { void initialize().then(run).catch(() => undefined) },
                } }))
              })
            }
            run()
            release()
          },
        }
      },
    }
    const entry = {
      open() {
        openers++
        let closed = false
        return { ...api, close() {
          if (closed) return
          closed = true
          openers--
          sweep()
        } }
      },
    }
    return entry
  }

  // Every composer of a chat in this tab edits one draft. While a send is
  // unacknowledged, storage keeps its source for recovery after a reload, so
  // the draft stays in memory until storage holds what it shows.
  const drafts = new Map<string, ReturnType<typeof createDraft>>()
  /** Opens the draft for `key`; `close` releases it. */
  const draft = (key: string) => {
    let entry = drafts.get(key)
    if (!entry) {
      entry = createDraft(key)
      drafts.set(key, entry)
    }
    return entry.open()
  }

  return {
    draft,
    async settled() { while (pending.size) await Promise.allSettled([...pending]); await repository.settled() },
    dispose() {
      disposed = true
      maybeDispose()
    },
  }
}
export type PromptPersistence = ReturnType<typeof createPromptPersistence>
